import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";

export default function Detail() {
  const { id } = useParams();
  const movie = movies.find((m) => m.id === id);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (movie) {
      document.title = `${movie.title} - 诱人的飞行`;
    }
    return () => {
      document.title = "诱人的飞行 - 最新影视内容与剧情介绍";
    };
  }, [movie]);

  const related = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id)
      .sort((a, b) => {
        const aScore = (a.type === movie.type ? 2 : 0) + a.genres.filter((g) => movie.genres.includes(g)).length;
        const bScore = (b.type === movie.type ? 2 : 0) + b.genres.filter((g) => movie.genres.includes(g)).length;
        return bScore - aScore || b.rating - a.rating;
      })
      .slice(0, 4);
  }, [movie]);

  const handleCopy = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <i className="ri-file-unknow-line text-5xl text-foreground-400 mb-5 inline-block" aria-hidden="true"></i>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">该影视内容未找到</h1>
        <p className="text-sm text-foreground-600 mb-6">你访问的作品可能已被移除，或链接不正确。</p>
        <Link
          to="/category"
          className="inline-flex px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap"
        >
          返回影视分类
        </Link>
      </div>
    );
  }

  const typeLabel = movie.type === "movie" ? "电影" : "电视剧";

  return (
    <div className="w-full">
      {/* 主图区 */}
      <div className="relative w-full h-[360px] md:h-[520px] bg-primary-950 overflow-hidden">
        <img
          src={movie.backdrop}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} 影视内容 剧情介绍`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30"></div>

        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 md:px-8 pb-6 md:pb-10">
            <nav className="text-xs text-background-300 mb-3 flex items-center gap-1.5 flex-wrap">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <Link to={`/category?type=${movie.type}`} className="hover:text-accent-400 transition-colors">{typeLabel}</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-background-100">{movie.title}</span>
            </nav>
            <div className="flex items-start gap-4 md:gap-6">
              <img
                src={movie.cover}
                alt={`${movie.title} 海报`}
                title={`${movie.title} 海报`}
                className="hidden sm:block w-[110px] md:w-[150px] aspect-[2/3] object-cover object-top rounded-lg border border-background-50/20 shrink-0"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-bold">{typeLabel}</span>
                  <span className="text-accent-400 text-sm font-bold flex items-center gap-1">
                    <i className="ri-fire-fill text-xs" aria-hidden="true"></i>
                    热度 {movie.rating.toFixed(1)}
                  </span>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">{movie.title}</h1>
                <p className="text-sm text-background-200 max-w-2xl leading-relaxed mb-4 line-clamp-2">{movie.summary}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setShareOpen(true)}
                    className="px-4 py-2 rounded-md bg-background-50/15 hover:bg-background-50/25 border border-background-50/25 text-background-50 text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-share-forward-line" aria-hidden="true"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* 主内容 */}
          <div className="flex-1 min-w-0">
            <section className="mb-8">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded bg-accent-500"></span>
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose whitespace-pre-line">{movie.synopsis}</p>
            </section>

            <section>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded bg-accent-500"></span>
                作品标签
              </h2>
              <div className="flex flex-wrap gap-2">
                {[...movie.genres, ...movie.tags].map((t, i) => (
                  <span
                    key={`${t}-${i}`}
                    className={`px-3 py-1.5 rounded-full text-xs ${
                      i < movie.genres.length ? "bg-primary-100 text-primary-800" : "bg-secondary-100 text-secondary-900"
                    }`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </section>
          </div>

          {/* 基本信息 */}
          <aside className="lg:w-[320px] shrink-0">
            <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
              <h2 className="text-base font-heading font-bold text-foreground-950 mb-4">基本信息</h2>
              <dl className="space-y-3.5 text-sm">
                {[
                  { label: "导演", value: movie.director },
                  { label: "主演", value: movie.cast.join(" / ") },
                  { label: "类型", value: movie.genres.join(" / ") },
                  { label: "国家/地区", value: movie.region },
                  { label: "上映时间", value: `${movie.year}年` },
                  { label: movie.type === "tv" ? "集数" : "片长", value: movie.duration },
                ].map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="text-foreground-500 shrink-0 w-20">{row.label}</dt>
                    <dd className="text-foreground-900 flex-1">{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 pt-5 border-t border-background-300 text-xs text-foreground-500 leading-relaxed">
                <i className="ri-information-line mr-1" aria-hidden="true"></i>
                本站仅提供影视资料与剧情介绍，不提供播放与下载。
              </div>
            </div>
          </aside>
        </div>

        {/* 相关推荐 */}
        {related.length > 0 && (
          <section className="mt-10 md:mt-14">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 rounded bg-accent-500"></span>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* 分享弹窗 */}
      {shareOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <button
            type="button"
            className="absolute inset-0 bg-foreground-950/50 cursor-pointer"
            onClick={() => setShareOpen(false)}
            aria-label="关闭分享"
          ></button>
          <div className="relative w-full max-w-sm bg-background-50 rounded-lg border border-background-200 p-6 animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg text-foreground-950">分享《{movie.title}》</h3>
              <button
                type="button"
                onClick={() => setShareOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div className="flex items-center gap-2 p-2 pl-3 rounded-md bg-background-100 border border-background-200 mb-4">
              <span className="text-xs text-foreground-600 truncate flex-1">{window.location.href}</span>
              <button
                type="button"
                onClick={handleCopy}
                className="px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                复制链接
              </button>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {[
                { icon: "ri-wechat-line", label: "微信" },
                { icon: "ri-weibo-line", label: "微博" },
                { icon: "ri-link", label: "链接" },
                { icon: "ri-qr-code-line", label: "二维码" },
              ].map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={handleCopy}
                  className="flex flex-col items-center gap-2 py-3 rounded-md bg-background-100 hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  <i className={`${s.icon} text-xl text-primary-600`} aria-hidden="true"></i>
                  <span className="text-xs text-foreground-700">{s.label}</span>
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-foreground-500 text-center leading-relaxed">
              分享为前端演示功能，仅支持复制当前页面链接。
            </p>
            {copied && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium animate-fade-in whitespace-nowrap">
                链接已复制
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}